/**
 * PickingPrintModal - Modern print modal for picking operations
 * Uses printingService.ts with 4-level hierarchical configuration
 */
import React, { useState, useEffect } from 'react';
import {
  X, Tag, Package, FileText, MapPin, Loader2, Check,
  AlertCircle, ChevronLeft, Minus, Plus
} from 'lucide-react';
import { printingService } from '../../../../../services/printingService';
import {
  PrintItemLabelWithSORequest,
  PrintPackingSlipRequest,
  PrintLocationLabelRequest,
  PrintGenericLabelRequest,
  PrintResult,
  PackingSlipLine,
  CreatePackageLabelsRequest,
  CreatePackageLabelsResponse,
  Report
} from '../../../../../types/printing';
import { PListDetailsDto, PListLineDto } from '../../../../../types/picking';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { usePrinterFallback } from '../../../../../hooks/usePrinterFallback';
import { FreeTextFieldsModal, extractFreeTextFields } from '../../../../common/FreeTextFieldsModal';

// Hvilken print-tjeneste-metode som dispatcher for hver ReportType
type PrintDispatch = 'item' | 'itemWithPO' | 'itemWithSO' | 'package' | 'packingSlip' | 'location' | 'generic';

interface ReportTypeMetadata {
  dispatch: PrintDispatch;
  typeNameKey: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  hoverColor: string;
}

/**
 * Mapper Reports.ReportType (1, 2, 3, 4, 10, 11, 40, 41 osv.) til UI-metadata og
 * dispatch-strategi. Returnerer null for typer som ikke skal vises i picking-printen
 * (f.eks. SSCC, sorteringskasse — har egne dialoger).
 */
const getReportMetadata = (reportType: number): ReportTypeMetadata | null => {
  // ReportType 60-69 = «Diverse» — generiske etiketter fra custom LabelDesigner-mal (opprinnelig Norlux P5)
  if (reportType >= 60 && reportType <= 69) {
    return { dispatch: 'generic', typeNameKey: 'warehouse.modules.picking.print.typeGenericLabel',
      icon: <FileText size={28} />, color: 'text-slate-600',
      bgColor: 'bg-slate-50', hoverColor: 'hover:bg-slate-100' };
  }
  switch (reportType) {
    case 1:
      return { dispatch: 'item', typeNameKey: 'warehouse.modules.picking.print.typeItemLabel',
        icon: <Tag size={28} />, color: 'text-blue-600',
        bgColor: 'bg-blue-50', hoverColor: 'hover:bg-blue-100' };
    case 2:
      return { dispatch: 'itemWithPO', typeNameKey: 'warehouse.modules.picking.print.typeItemLabelWithPO',
        icon: <Tag size={28} />, color: 'text-indigo-600',
        bgColor: 'bg-indigo-50', hoverColor: 'hover:bg-indigo-100' };
    case 3:
      return { dispatch: 'itemWithSO', typeNameKey: 'warehouse.modules.picking.print.typeItemLabelWithSO',
        icon: <Tag size={28} />, color: 'text-purple-600',
        bgColor: 'bg-purple-50', hoverColor: 'hover:bg-purple-100' };
    case 4:
    case 5:
      return { dispatch: 'location', typeNameKey: 'warehouse.modules.picking.print.typeLocationLabel',
        icon: <MapPin size={28} />, color: 'text-teal-600',
        bgColor: 'bg-teal-50', hoverColor: 'hover:bg-teal-100' };
    case 10:
    case 11:
      return { dispatch: 'packingSlip', typeNameKey: 'warehouse.modules.picking.print.typePackingSlip',
        icon: <FileText size={28} />, color: 'text-green-600',
        bgColor: 'bg-green-50', hoverColor: 'hover:bg-green-100' };
    case 40:
    case 41:
      return { dispatch: 'package', typeNameKey: 'warehouse.modules.picking.print.typePackageLabel',
        icon: <Package size={28} />, color: 'text-orange-600',
        bgColor: 'bg-orange-50', hoverColor: 'hover:bg-orange-100' };
    default:
      return null;
  }
};

/**
 * Slår opp metadata for en dispatch-type ved å finne første rapport av den typen
 * og hente metadata via getReportMetadata. Brukes i steg 1 før konkret rapport er valgt.
 */
const getMetadataForDispatch = (dispatch: PrintDispatch, reports: Report[]): ReportTypeMetadata | null => {
  for (const r of reports) {
    const meta = getReportMetadata(r.reportType);
    if (meta?.dispatch === dispatch) return meta;
  }
  return null;
};

interface PickingPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  pickingDetails: PListDetailsDto | null;
  activeLine: PListLineDto | null;
}

export const PickingPrintModal: React.FC<PickingPrintModalProps> = ({
  isOpen,
  onClose,
  pickingDetails,
  activeLine
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  // Steg 1: brukeren velger en type (item, location, package, packingSlip, ...)
  const [selectedDispatch, setSelectedDispatch] = useState<PrintDispatch | null>(null);
  // Steg 2: brukeren velger en konkret rapport (Reports-rad) av valgt type
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [availableReports, setAvailableReports] = useState<Report[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printResult, setPrintResult] = useState<PrintResult | null>(null);
  const [packageResult, setPackageResult] = useState<CreatePackageLabelsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state for editable fields
  const [copies, setCopies] = useState(1);
  const [itemNo, setItemNo] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [quantity, setQuantity] = useState<number | null>(null);
  const [batch, setBatch] = useState('');
  const [locationName, setLocationName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [orderNoERP, setOrderNoERP] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  // P5: fritekst for generisk etikett-gruppe (ReportType 60-69)
  const [freeText, setFreeText] = useState('');

  // Frifelt-modal (FreeText1-5): åpnes før utskrift når valgt rapportmal bruker frifelt
  const [freeTextModalOpen, setFreeTextModalOpen] = useState(false);

  // Avledet metadata for valgt rapport (eller valgt type i steg 1)
  const selectedMetadata = selectedReport
    ? getReportMetadata(selectedReport.reportType)
    : (selectedDispatch ? getMetadataForDispatch(selectedDispatch, availableReports) : null);

  // Tilgjengelige rapporter filtrert på valgt dispatch-type (for dropdown i steg 2)
  const reportsForSelectedType = selectedDispatch
    ? availableReports.filter((r) => {
        const meta = getReportMetadata(r.reportType);
        return meta?.dispatch === selectedDispatch;
      })
    : [];

  // Unike dispatch-typer som finnes i tilgjengelige rapporter (for steg 1-grid)
  const availableDispatchTypes: PrintDispatch[] = Array.from(
    new Set(
      availableReports
        .map((r) => getReportMetadata(r.reportType)?.dispatch)
        .filter((d): d is PrintDispatch => d !== undefined)
    )
  );

  // Reset state when modal opens/closes + last rapporter fra Reports-tabellen
  useEffect(() => {
    if (!isOpen || !user?.companyNo) return;

    setSelectedDispatch(null);
    setSelectedReport(null);
    setPrintResult(null);
    setPackageResult(null);
    setError(null);
    setCopies(1);
    prefillFromActiveLine();

    let cancelled = false;
    setLoadingReports(true);
    setReportsError(null);
    printingService.getReports(user.companyNo, undefined, true)
      .then((reports) => {
        if (cancelled) return;
        // Vis kun rapporter med en gjenkjent type for picking-flyten
        const filtered = reports.filter((r) => getReportMetadata(r.reportType) !== null);
        // Sorter på type, deretter ReportID
        filtered.sort((a, b) => a.reportType - b.reportType || a.reportID - b.reportID);
        setAvailableReports(filtered);
      })
      .catch((err) => {
        if (!cancelled) setReportsError(err?.message || t('warehouse.modules.picking.print.errorLoadReports'));
      })
      .finally(() => {
        if (!cancelled) setLoadingReports(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.companyNo, activeLine]);

  // Auto-close modal after successful package label creation
  useEffect(() => {
    if (packageResult?.success) {
      const timer = setTimeout(() => {
        onClose();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [packageResult, onClose]);

  // Prefill form from active line
  const prefillFromActiveLine = () => {
    if (activeLine) {
      setItemNo(activeLine.itemNo || '');
      setQuantity(activeLine.qtyPicked || activeLine.qty);
      setLocationName(activeLine.binCode || '');
      setCustomerName(activeLine.customerName || '');
      setOrderNoERP(activeLine.orderNoERP || '');
    } else if (pickingDetails?.lines?.length) {
      // Use first line as fallback
      const firstLine = pickingDetails.lines[0];
      setItemNo(firstLine.itemNo || '');
      setQuantity(firstLine.qty);
      setLocationName(firstLine.binCode || '');
      setCustomerName(firstLine.customerName || '');
      setOrderNoERP(firstLine.orderNoERP || '');
    }
  };

  // Update form when selected report changes
  useEffect(() => {
    if (selectedReport) {
      prefillFromActiveLine();
    }
  }, [selectedReport]);

  const handleBack = () => {
    setSelectedDispatch(null);
    setSelectedReport(null);
    setPrintResult(null);
    setPackageResult(null);
    setError(null);
  };

  /**
   * Når brukeren velger en type i steg 1, går vi videre til steg 2 og auto-velger
   * første rapport av den typen i dropdownen. Brukeren kan så bytte til en annen.
   */
  const handleSelectDispatch = (dispatch: PrintDispatch) => {
    setSelectedDispatch(dispatch);
    const firstOfType = availableReports.find(
      (r) => getReportMetadata(r.reportType)?.dispatch === dispatch
    );
    setSelectedReport(firstOfType || null);
    setPrintResult(null);
    setPackageResult(null);
    setError(null);
  };

  const printerFallback = usePrinterFallback();

  /**
   * Bygger og kjører selve print-kallet basert på valgt rapport. Sender
   * `reportId: selectedReport.reportID` som override slik at backend bruker
   * rapportens egen LayoutJson hvis satt, ellers hardkodet generator.
   */
  const runPrint = async (printerOverride?: number, freeTextVals?: Record<string, string>): Promise<PrintResult> => {
    if (!user?.companyNo || !selectedReport || !selectedMetadata) {
      return { success: false, message: t('warehouse.modules.picking.print.errorMissingCompanyOrReport'), labelsPrinted: 0 };
    }

    const reportId = selectedReport.reportID;

    switch (selectedMetadata.dispatch) {
      case 'item':
        return await printingService.printItemLabel(user.companyNo, {
          reportId,
          itemNo,
          itemDescription,
          quantity,
          batch: batch || undefined,
          locationName: locationName || undefined,
          numOfCopies: copies,
          printerIdOverride: printerOverride,
          freeTextValues: freeTextVals
        });

      case 'itemWithPO':
        return await printingService.printItemLabelWithPO(user.companyNo, {
          reportId,
          itemNo,
          itemDescription,
          quantity,
          batch: batch || undefined,
          locationName: locationName || undefined,
          pOrderNo: activeLine?.orderNo,
          numOfCopies: copies,
          printerIdOverride: printerOverride,
          freeTextValues: freeTextVals
        } as any);

      case 'itemWithSO':
        return await printingService.printItemLabelWithSO(user.companyNo, {
          reportId,
          itemNo,
          itemDescription,
          quantity,
          batch: batch || undefined,
          locationName: locationName || undefined,
          orderNo: activeLine?.orderNo,
          orderNoERP: orderNoERP || undefined,
          customerName: customerName || undefined,
          numOfCopies: copies,
          printerIdOverride: printerOverride,
          freeTextValues: freeTextVals
        } as PrintItemLabelWithSORequest);

      case 'packingSlip': {
        const slipLines: PackingSlipLine[] = pickingDetails?.lines.map((line, idx) => ({
          lineNo: idx + 1,
          itemNo: line.itemNo || '',
          itemDescription: '',
          qtyOrdered: line.qty,
          qtyPicked: line.qtyPicked,
          location: line.binCode || undefined
        })) || [];

        return await printingService.printPackingSlip(user.companyNo, {
          reportId,
          pListId: pickingDetails?.header.pListID || 0,
          customerName: customerName || undefined,
          deliveryAddress: deliveryAddress || undefined,
          packingDate: new Date().toISOString(),
          lines: slipLines,
          numOfCopies: copies,
          printerIdOverride: printerOverride,
          freeTextValues: freeTextVals
        } as PrintPackingSlipRequest);
      }

      case 'location':
        return await printingService.printLocationLabel(user.companyNo, {
          reportId,
          locationId: locationName,
          locationName: locationName || undefined,
          binCode: locationName || undefined,
          includeBarcode: true,
          numOfCopies: copies,
          printerIdOverride: printerOverride,
          freeTextValues: freeTextVals
        } as PrintLocationLabelRequest);

      case 'generic':
        return await printingService.printGenericLabel(user.companyNo, {
          reportId,
          freeText: freeText || undefined,
          numOfCopies: copies,
          printerIdOverride: printerOverride,
          freeTextValues: freeTextVals
        } as PrintGenericLabelRequest);

      default:
        return { success: false, message: t('warehouse.modules.picking.print.errorUnknownPrintType'), labelsPrinted: 0 };
    }
  };

  /**
   * Frifelt malen bruker (FreeText1-5). Pakke-flyten går via eget endepunkt uten
   * frifelt-støtte og gates derfor ikke.
   */
  const freeTextFields = selectedMetadata?.dispatch !== 'package'
    ? extractFreeTextFields(selectedReport?.layoutJson)
    : [];

  const handlePrint = () => {
    if (!user?.companyNo || !selectedReport || !selectedMetadata) return;
    // Malen har frifelt → operatøren må fylle inn og bekrefte i modalen først
    if (freeTextFields.length > 0) {
      setFreeTextModalOpen(true);
      return;
    }
    void executePrint(undefined);
  };

  /**
   * Kjøres fra frifelt-modalen. Modalen forblir åpen — resultatet returneres og vises
   * der, slik at operatøren kan justere teksten og skrive ut på nytt.
   * `printerIdOverride` er skrivervalget i modalen (null = automatisk oppløsning).
   */
  const handleFreeTextConfirm = async (values: Record<string, string>, printerIdOverride: number | null) => {
    return await executePrint(values, printerIdOverride ?? undefined);
  };

  const executePrint = async (
    freeTextVals?: Record<string, string>,
    printerOverride?: number
  ): Promise<{ success: boolean; message?: string } | undefined> => {
    if (!user?.companyNo || !selectedReport || !selectedMetadata) return;

    setIsPrinting(true);
    setError(null);
    setPrintResult(null);
    setPackageResult(null);

    // Pakke-flyten bruker eget endepunkt (createAndPrintPackageLabels) og har annen
    // result-form — håndteres separat utenfor fallback-mekanismen.
    if (selectedMetadata.dispatch === 'package') {
      try {
        const packageRequest: CreatePackageLabelsRequest = {
          numberOfLabels: copies,
          pListID: pickingDetails?.header.pListID || 0,
          orderNo: activeLine?.orderNo || pickingDetails?.lines[0]?.orderNo || 0,
          pickType: 'Standalone',
          customerName: customerName || undefined,
          orderNoERP: orderNoERP || undefined,
          deliveryAddress: deliveryAddress || undefined,
          printLabels: true,
          // Bugfiks: send operatørens valgte kolli-rapport som override — ellers ble valget kastet
          // og backend brukte alltid standard/innebygd kolli-etikett.
          reportIdOverride: selectedReport.reportID
        };

        const packageResponse = await printingService.createAndPrintPackageLabels(packageRequest);
        setPackageResult(packageResponse);
      } catch (err: any) {
        console.error('Print error:', err);
        setError(err.message || t('warehouse.modules.picking.print.errorPrinting'));
      } finally {
        setIsPrinting(false);
      }
      return;
    }

    try {
      let outcome: PrintResult | undefined;
      await printerFallback.tryPrint(
        () => runPrint(printerOverride, freeTextVals),
        (printerId) => runPrint(printerId, freeTextVals),
        (result) => {
          outcome = result;
          setPrintResult(result);
        }
      );
      return outcome ? { success: outcome.success, message: outcome.message } : undefined;
    } catch (err: any) {
      console.error('Print error:', err);
      setError(err.message || 'Feil ved utskrift');
      return { success: false, message: err.message };
    } finally {
      setIsPrinting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            {selectedDispatch && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label={t('warehouse.modules.picking.print.backToSelection')}
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedDispatch ? (selectedMetadata ? t(selectedMetadata.typeNameKey) : t('warehouse.modules.picking.print.selectReport')) : t('warehouse.modules.picking.print.selectPrintType')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label={t('common.buttons.close')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {!selectedDispatch ? (
            // Steg 1: type-velger — viser kun typer som har minst én rapport tilgjengelig
            <div className="grid gap-3">
              {loadingReports && (
                <div className="flex items-center gap-2 p-4 text-gray-500">
                  <Loader2 size={18} className="animate-spin" />
                  <span>{t('warehouse.modules.picking.print.loadingReports')}</span>
                </div>
              )}
              {reportsError && (
                <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm">
                  {reportsError}
                </div>
              )}
              {!loadingReports && !reportsError && availableReports.length === 0 && (
                <div className="p-4 bg-gray-50 text-gray-600 rounded-lg text-sm">
                  {t('warehouse.modules.picking.print.noReportsFound')}
                </div>
              )}
              {availableDispatchTypes.map((dispatch) => {
                const meta = getMetadataForDispatch(dispatch, availableReports);
                if (!meta) return null;
                const count = availableReports.filter(
                  (r) => getReportMetadata(r.reportType)?.dispatch === dispatch
                ).length;
                return (
                  <button
                    key={dispatch}
                    onClick={() => handleSelectDispatch(dispatch)}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 border-transparent
                      ${meta.bgColor} ${meta.hoverColor} transition-all duration-200
                      hover:border-current hover:shadow-md`}
                  >
                    <div className={`${meta.color} p-3 bg-white rounded-xl shadow-sm`}>
                      {meta.icon}
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <h3 className={`font-semibold ${meta.color} truncate`}>{t(meta.typeNameKey)}</h3>
                      <p className="text-sm text-gray-600">
                        {count === 1
                          ? t('warehouse.modules.picking.print.reportsAvailableOne', { 0: count })
                          : t('warehouse.modules.picking.print.reportsAvailableOther', { 0: count })}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            // Detail/edit view
            <div className="space-y-4">
              {/* Success/Error messages */}
              {printResult && (
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  printResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {printResult.success ? (
                    <Check size={20} className="flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium">{printResult.success ? t('warehouse.modules.picking.print.printSuccess') : t('warehouse.modules.picking.print.errorPrinting')}</p>
                    <p className="text-sm">{printResult.message}</p>
                    {printResult.labelsPrinted > 0 && (
                      <p className="text-sm mt-1">{t('warehouse.modules.picking.print.labelsPrinted', { 0: printResult.labelsPrinted })}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Package creation result */}
              {packageResult && (
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  packageResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {packageResult.success ? (
                    <Check size={20} className="flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium">
                      {packageResult.success ? t('warehouse.modules.picking.print.packageLabelsCreated') : t('warehouse.modules.picking.print.errorCreatingPackage')}
                    </p>
                    <p className="text-sm">{packageResult.message}</p>
                    {packageResult.success && packageResult.createdPackages.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs font-medium">{t('warehouse.modules.picking.print.createdPackages')}</p>
                        {packageResult.createdPackages.map((pkg) => (
                          <p key={pkg.packageID} className="text-xs">
                            {pkg.displayText} ({t('warehouse.modules.picking.print.idLabel')}: {pkg.packageID})
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="p-4 rounded-lg bg-red-50 text-red-800 flex items-start gap-3">
                  <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">{t('common.status.error')}</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Form fields based on selected type */}
              <div className="space-y-4">
                {/* Rapport-velger for valgt type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('warehouse.modules.picking.print.report')}
                  </label>
                  <select
                    value={selectedReport?.reportID ?? ''}
                    onChange={(e) => {
                      const id = parseInt(e.target.value, 10);
                      const r = reportsForSelectedType.find((x) => x.reportID === id);
                      setSelectedReport(r ?? null);
                      setPrintResult(null);
                      setPackageResult(null);
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                    disabled={reportsForSelectedType.length === 0}
                  >
                    {reportsForSelectedType.length === 0 ? (
                      <option value="">{t('warehouse.modules.picking.print.noReportsForType')}</option>
                    ) : (
                      reportsForSelectedType.map((r) => (
                        <option key={r.reportID} value={r.reportID}>
                          {r.description} ({t('warehouse.modules.picking.print.idLabel')} {r.reportID})
                        </option>
                      ))
                    )}
                  </select>
                </div>

                {/* Copies - always shown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {selectedMetadata?.dispatch === 'package' ? t('warehouse.modules.picking.print.numPackageLabels') : t('warehouse.modules.picking.print.numCopies')}
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setCopies(Math.max(1, copies - 1))}
                      className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                      disabled={copies <= 1}
                    >
                      <Minus size={18} />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={copies}
                      onChange={(e) => setCopies(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                      className="w-20 text-center p-3 border border-gray-300 rounded-lg text-lg font-semibold"
                    />
                    <button
                      type="button"
                      onClick={() => setCopies(Math.min(100, copies + 1))}
                      className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                      disabled={copies >= 100}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>

                {/* Item fields */}
                {(selectedMetadata?.dispatch === 'item' || selectedMetadata?.dispatch === 'itemWithSO' || selectedMetadata?.dispatch === 'itemWithPO') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.fields.itemNumber')}
                      </label>
                      <input
                        type="text"
                        value={itemNo}
                        onChange={(e) => setItemNo(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder={t('warehouse.modules.picking.print.itemNoPlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.fields.quantity')}
                      </label>
                      <input
                        type="number"
                        value={quantity || ''}
                        onChange={(e) => setQuantity(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder={t('warehouse.modules.picking.print.quantityPlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('warehouse.modules.picking.print.batchLot')}
                      </label>
                      <input
                        type="text"
                        value={batch}
                        onChange={(e) => setBatch(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder={t('warehouse.modules.picking.print.batchPlaceholder')}
                      />
                    </div>
                  </>
                )}

                {/* Order fields */}
                {(selectedMetadata?.dispatch === 'itemWithSO' || selectedMetadata?.dispatch === 'itemWithPO') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('warehouse.modules.picking.print.customerName')}
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder={t('warehouse.modules.picking.print.customerNamePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('warehouse.modules.picking.print.orderNoErp')}
                      </label>
                      <input
                        type="text"
                        value={orderNoERP}
                        onChange={(e) => setOrderNoERP(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder={t('warehouse.modules.picking.print.orderNoErpPlaceholder')}
                      />
                    </div>
                  </>
                )}

                {/* Package fields */}
                {selectedMetadata?.dispatch === 'package' && (
                  <>
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <p className="text-sm text-orange-800 font-medium">
                        {t('warehouse.modules.picking.print.creatingPackageRows', { 0: copies })}
                      </p>
                      <p className="text-xs text-orange-700 mt-1">
                        {t('warehouse.modules.picking.print.packageRowsInfo', { 0: pickingDetails?.header.pListID ?? '', 1: activeLine?.orderNo || pickingDetails?.lines[0]?.orderNo || '' })}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('warehouse.modules.picking.print.customerName')}
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('warehouse.modules.picking.print.deliveryAddress')}
                      </label>
                      <textarea
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        rows={2}
                        placeholder={t('warehouse.modules.picking.print.deliveryAddressPlaceholder')}
                      />
                    </div>
                  </>
                )}

                {/* Packing slip info */}
                {selectedMetadata?.dispatch === 'packingSlip' && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      {t('warehouse.modules.picking.print.packingSlipInfo', { 0: pickingDetails?.header.pListID ?? '' })}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('warehouse.modules.picking.print.linesIncluded', { 0: pickingDetails?.lines.length || 0 })}
                    </p>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('warehouse.modules.picking.print.customerNameOptional')}
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {/* Location fields */}
                {selectedMetadata?.dispatch === 'location' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('warehouse.modules.picking.print.locationBinCode')}
                    </label>
                    <input
                      type="text"
                      value={locationName}
                      onChange={(e) => setLocationName(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      placeholder={t('warehouse.modules.picking.print.locationPlaceholder')}
                    />
                  </div>
                )}

                {/* P5: fritekst for generisk etikett (valgfri — la stå tom for hardkodet-tekst-etiketter) */}
                {selectedMetadata?.dispatch === 'generic' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('warehouse.modules.picking.print.freeText', {}, 'Fritekst (valgfri)')}
                    </label>
                    <textarea
                      value={freeText}
                      onChange={(e) => setFreeText(e.target.value)}
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      placeholder={t('warehouse.modules.picking.print.freeTextPlaceholder', {}, 'Tekst som skal skrives på etiketten...')}
                    />
                  </div>
                )}

                {/* Context info */}
                {activeLine && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                    <p className="font-medium">{t('warehouse.modules.picking.print.fromActiveLine')}</p>
                    <p>{t('warehouse.modules.picking.print.itemLocationLine', { 0: activeLine.itemNo, 1: activeLine.binCode })}</p>
                  </div>
                )}
              </div>

              {/* Print button */}
              <button
                onClick={handlePrint}
                disabled={isPrinting || !selectedReport}
                className={`w-full py-4 rounded-xl font-semibold text-white transition-all
                  ${(isPrinting || !selectedReport)
                    ? 'bg-gray-400 cursor-not-allowed'
                    : `${selectedMetadata?.color.replace('text-', 'bg-')} hover:opacity-90`
                  }`}
              >
                {isPrinting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={20} className="animate-spin" />
                    {t('warehouse.modules.picking.print.printing')}
                  </span>
                ) : (
                  `${t('common.buttons.print')} ${selectedMetadata ? t(selectedMetadata.typeNameKey) : ''}`
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Fallback-modal når ingen fast skriver er konfigurert */}
      {printerFallback.modal}

      {/* Frifelt-modal: operatør fyller inn FreeText1-5 med live forhåndsvisning før utskrift */}
      {user?.companyNo && selectedReport?.layoutJson && (
        <FreeTextFieldsModal
          isOpen={freeTextModalOpen}
          companyNo={user.companyNo}
          layoutJson={selectedReport.layoutJson}
          fields={freeTextFields}
          reportDescription={selectedReport.description}
          context={{
            itemNo: itemNo || undefined,
            orderNo: activeLine?.orderNo,
            binCode: locationName || undefined,
            pListID: pickingDetails?.header.pListID
          }}
          onCancel={() => setFreeTextModalOpen(false)}
          onConfirm={handleFreeTextConfirm}
        />
      )}
    </div>
  );
};

export default PickingPrintModal;
